import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as adminApi from '../api/admin';
import { generateTraceId, logUserAction } from '../utils/trace';

function GodModePage() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Assign user form
  const [assignEmail, setAssignEmail] = useState('');
  const [assignTenantId, setAssignTenantId] = useState('');
  const [assignRole, setAssignRole] = useState('user');

  // Redirect if not god mode
  useEffect(() => {
    if (user && !user.isGodMode) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Fetch data on mount
  useEffect(() => {
    if (user?.isGodMode) {
      const traceId = generateTraceId();
      logUserAction(traceId, 'page_load', { page: 'god-mode' });
      fetchUsers();
      fetchTenants();
    }
  }, [user]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const traceId = generateTraceId();
      const data = await adminApi.getAllUsers({ traceId });
      setUsers(data || []);
    } catch (err) {
      setError('Failed to fetch users: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const traceId = generateTraceId();
      const data = await adminApi.getAllTenants({ traceId });
      setTenants(data || []);
    } catch (err) {
      setError('Failed to fetch tenants: ' + err.message);
    }
  };

  const handleAssignUser = async (e) => {
    e.preventDefault();
    setError(null);
    const traceId = generateTraceId();
    logUserAction(traceId, 'assign_user', { email: assignEmail, tenantId: assignTenantId, role: assignRole });
    try {
      await adminApi.assignUserToAnyTenant(assignEmail, assignTenantId, assignRole, { traceId });
      setSuccessMessage(`User "${assignEmail}" assigned to "${assignTenantId}" as ${assignRole}`);
      setAssignEmail('');
      setAssignTenantId('');
      setAssignRole('user');
      fetchUsers();
    } catch (err) {
      setError('Failed to assign user: ' + err.message);
    }
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (!user?.isGodMode) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
        <p className="text-gray-600">You need god mode privileges to access this page.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6 text-purple-700">God Mode</h1>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {error}
          <button onClick={() => setError(null)} className="ml-4 font-bold">&times;</button>
        </div>
      )}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
          {successMessage}
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex gap-4">
          {['users', 'tenants', 'assign'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-4 px-2 font-medium transition capitalize ${
                activeTab === tab
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="font-bold text-gray-900">All Users ({users.length})</h2>
            <button
              onClick={fetchUsers}
              className="text-purple-600 hover:text-purple-800 font-medium text-sm"
            >
              Refresh
            </button>
          </div>
          {isLoading ? (
            <div className="p-8 text-center text-gray-600">Loading...</div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-gray-600">No users found.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    God Mode
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((u) => (
                  <tr key={u.user_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{u.email}</td>
                    <td className="px-6 py-4 text-sm">
                      {u.is_god_mode ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          God
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Tenants Tab */}
      {activeTab === 'tenants' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h2 className="font-bold text-gray-900">All Tenants ({tenants.length})</h2>
              <p className="text-sm text-gray-500 mt-1">
                Tenants are created when a Salesforce admin logs in for the first time
              </p>
            </div>
            <button
              onClick={fetchTenants}
              className="text-purple-600 hover:text-purple-800 font-medium text-sm"
            >
              Refresh
            </button>
          </div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Tenant ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Parent
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tenants.map((t) => (
                <tr key={t.tenant_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-mono text-gray-900">
                    {t.tenant_id}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{t.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {t.parent_tenant_id || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Assign Tab */}
      {activeTab === 'assign' && (
        <div className="max-w-md">
          {/* Assign User Form */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4 text-gray-900">Assign User to Tenant</h2>
            <p className="text-sm text-gray-500 mb-4">
              Add an existing user to a tenant, or create a new user and assign them.
            </p>
            <form onSubmit={handleAssignUser}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={assignEmail}
                  onChange={(e) => setAssignEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="user@example.com"
                />
                <p className="mt-1 text-xs text-gray-500">User will be created if they don't exist</p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tenant
                </label>
                <select
                  value={assignTenantId}
                  onChange={(e) => setAssignTenantId(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="">Select tenant...</option>
                  {tenants.map((t) => (
                    <option key={t.tenant_id} value={t.tenant_id}>
                      {t.name} ({t.tenant_id})
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={assignRole}
                  onChange={(e) => setAssignRole(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="viewer">Viewer</option>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <button
                type="submit"
                className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium"
              >
                Assign User
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default GodModePage;
